<template>
  <div class="bg-modal w-maxC" @click.stop>

      <!-- Bio Groups (Icons) -->
      <div class="row gap-06 mtop-01">
        <template v-for="(bioGroup, index) in bioGroupIcons" :key="index">
          <div style="position: relative;">
            <text-item style="position: absolute; margin-left: 1.7rem; margin-top: -0.3rem;" :txt="bioGroup.count" styles="main-m font-s"/> 
            <buttonGroup @click="currentEntryStore.setBioTab(bioGroup.groupKey)">
              <svg-item :icon="bioGroup.groupIcon" styles="size-l main-m"/>
            </buttonGroup>
            <!-- Underline current -->
            <div v-if="bioKey === bioGroup.groupKey" class="w-full line-h" style="position: absolute; margin-top: -2px; border-top-width: 3px; border-color: var(--main-l);"/>    
          </div>
        </template>

        <!-- Settings -->
        <dropdownBox v-if="bioKey" v-model="dropd" openOn='BioSettings' class="w-full">
          <buttonGroup class="mleft-30">
            <svg-item icon="settings" styles="size-m main-l"/>
          </buttonGroup>
          <template #dropd>
            <docBioPreferences :bioKey="bioKey" :bioIcon="bioIcon"/>
          </template>
        </dropdownBox>       

      </div>

      <!-- BioGroup suggestions -->
      <text-item v-if="!linkedBioGroups" class="mleft-08 mtop-20" :txt="t('common.loading')" styles="main-m"/>
      <div v-if="linkedBioGroups" class="col mtop-20 height-scroll pright-20">
        <template v-for="(group, groupIndex) in linkedBioGroups" :key="groupIndex">
          <template v-if="bioKey === group.groupKey">
            <!-- Grouped bio-switches -->
            <template v-for="(row, rowIndex) in linkedBioGroups[groupIndex].elements" :key="rowIndex">
              <listGroup :active="false" class="pad-0 w-full flexwrap" style="gap: 0.07rem;" @click="switchBioRowPrescription(row)">
                <div class="col">
                  <!-- Bio switch-buttons -->
                  <template v-for="(expr, exprIndex) in linkedBioGroups[groupIndex].elements[rowIndex]" :key="exprIndex">
                    <div class="row">
                      <div v-if="exprIndex !== 0" style="width: 1.5rem;"></div>
                      <switchGroup styles="padButton-s" style="padding: 0rem 0.5rem;" :active="expr.active" @click.stop="switchSingleBioPrescription(row, exprIndex)">
                        <div class="row">
                          <text-item :txt=expr.text styles="main-m" :class="expr.active ? 'txt-white' : ''" class="es-suggestion-txt"/>
                          <div v-if="expr.isMatched == true" class="mleft-04" @click.stop>
                            <itemModal v-if="currentEntryStore.showAnnotations" :center="true" :uiId="expr.originalText">
                              <img src="@/assets/images/shareIcons/bolt.svg" class="size-m main-l" :class="expr.active ? 'img-white' : 'img-grey'">
                              <template #modal>
                                <showAnnotations :content="expr"/>
                              </template>
                            </itemModal>
                          </div>
                        </div>
                      </switchGroup>
                    </div>
                  </template>
                </div>               
              </listGroup>
            </template>
          </template>
        </template>
      </div>        

  </div>    
</template>


<script setup>
import showAnnotations from '@/views/patientMain/showAnnotations.vue'
import docBioPreferences from './docBioPreferences.vue'

import { ref, computed, watch, onBeforeUnmount } from 'vue'
import gen from "@/functions/general.js"
import { useCurrentEntryStore } from '@/stores/currentEntry'
import patientapi from "@/api/patientapi"
import { useAppUserStore } from '@/stores/appUser'
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const appUserStore = useAppUserStore()
const currentEntryStore = useCurrentEntryStore()

const dropd = ref("")

const pendingToAdd = new Map()    // key: originalText.toLowerCase(), value: { conclusionGroupId, originalText }
const pendingToDelete = new Map() // key: expression_CclId, value: { expression_CclId, entryId }

const bioKey = computed(() => {
  return currentEntryStore.bioTab
})

const bioIcon = computed(() => {
  return BIO_LIST_TO_ICON_MAP[bioKey.value] || "person"
})

const currCclGroupId = ref(currentEntryStore.currentCcl?.conclusionGroupId.toString() || currentEntryStore.currentNullCclId.toString());

function switchBioRowPrescription(row) {
  const allPrescribed = row.every(expr => expr.active);
  row.forEach((expr, index) => {
    if (allPrescribed || !expr.active) {
      switchSingleBioPrescription(row, index);
    }
  });
}

function switchSingleBioPrescription(row, itemIndex) {
  const entryId = currentEntryStore.currentEntryId;
  const suggExpr = row[itemIndex]
  const originalText = suggExpr.originalText;

  if (suggExpr.active) {
    suggExpr.active = false
    // Check if this was just added (cancel out)
    if (pendingToAdd.has(originalText)) {
      pendingToAdd.delete(originalText)
      return;
    }
    //Delete only if expression_CclId
    if (suggExpr.expression_CclId) {
      pendingToDelete.set(suggExpr.expression_CclId, {
        expression_CclId: suggExpr.expression_CclId,
        entryId
      })
    }
  } else {
    suggExpr.active = true
    const conclusionGroupId = currCclGroupId.value
    // Check if this was just deleted (cancel out)
    if (suggExpr.expression_CclId && pendingToDelete.has(suggExpr.expression_CclId)) {
      pendingToDelete.delete(suggExpr.expression_CclId)
    } else {
      // Track addition
      pendingToAdd.set(originalText, {
        conclusionGroupId,
        originalText
      })
    }
  }
}

const bioGroupIcons = computed(() => {
  function getActiveCount(groupKey) {
    return linkedBioGroups.value?.find(item => item.groupKey === groupKey)?.elements
      .flat(2)
      .filter(item => item.active).length || 0
  }
  return Object.entries(BIO_LIST_TO_ICON_MAP).map(([groupKey, groupIcon]) => {
    const countNumber = linkedBioGroups.value ? getActiveCount(groupKey) : 0
    const count = countNumber > 0 ? countNumber.toString() : ""
    return { groupKey, groupIcon, count }
  })
})

const prescribedBioExprs = computed(() => {
  if (!currentEntryStore.hasCurrentEntry || !currentEntryStore.currentCcl) {return []}
  
  return currentEntryStore.currentCcl.bioPrescrArr;
})

const BIO_LIST_TO_ICON_MAP = {
  "general" : "person",
  "infectious" : "infectious",
  "bioChemical" : "biochemical",
  "endocrino" : "endoc",
  "hemato" : "hemato",  
  "onco" : "cancer", 
  "immuno" : "immunity",
  "allergo" : "allergy",  
  "toxico" : "toxic",
}

const linkedBioGroups = ref(null)
function createLinkedBioGroups() {
  if (!currentEntryStore.hasCurrentEntry) return
  if (!appUserStore.currentAppUserData) return
  //Create bioGroups
  const bioGroups = Object.keys(BIO_LIST_TO_ICON_MAP).map(key => {
    const { referenceText, elements } = appUserStore.currentAppUserData.preferences.bioPrescriptions[key]
    return {
      groupKey: key,
      elements: bioRefTextToSct(referenceText, elements)
    }
  })
  linkedBioGroups.value = bioGroups
}

function bioRefTextToSct(referenceText, elements) {
  return referenceText
    .split(/\n{2,}/g) //split by 2 or more occurences of \n
    .map(row => row.split('\n')
      .map(string => {
        let suggExpr = elements.find(expr => expr.originalText.trim().toLowerCase() === string.trim().toLowerCase())
        if (!suggExpr) {console.log(`Error. No reference found for: "${string}" in this array:`, elements);return null;}
        suggExpr = linkPrescribedExprToSugg(suggExpr)
        return suggExpr
      }).filter(item => item !== null)
    )
}

function linkPrescribedExprToSugg(suggExpr) {
  //Test if suggExpr = prescribed (by lowerCase match)
  let foundPrescribedExpr = prescribedBioExprs.value.find(prescribedExpr => 
    prescribedExpr.originalText.toLowerCase() === suggExpr.originalText.toLowerCase()
  )
  //Gest if suggExpr = prescribed (by sctLabKey match)
  //Some item have the same sctLabkey do not filter by sctLabkey to avoid multiple selection
  /**if (!foundPrescribedExpr && suggExpr.isMatched) {
    foundPrescribedExpr = prescribedBioExprs.value.find(prescribedExpr => 
      prescribedExpr.sctLabKey === suggExpr.sctLabKey
    )
  }*/
  //Add details
  if (foundPrescribedExpr) {
    const ret = gen.deepCopy(foundPrescribedExpr)
    ret.expression_CclId = foundPrescribedExpr.expressionCcl.expressionCclId
    ret.text = suggExpr.originalText
    ret.active = true
    return ret
  } else {
    const ret = gen.deepCopy(suggExpr)
    ret.text = suggExpr.originalText
    ret.active = false
    return ret
  }
}

async function fullReload() {
  const entryId = currentEntryStore.currentEntryId;
  const docId = currentEntryStore.currentDocId;  
  if (docId) {
    const fullPatientDoc = await patientapi.getPatientDocument(docId, entryId);
    currentEntryStore.updateCurrentDocData(fullPatientDoc);
  }    
  await currentEntryStore.reloadCurrentEntry();
}

async function flushPendingChanges() {
  // Process deletions in parallel
  await Promise.all(
    Array.from(pendingToDelete.values()).map(({ expression_CclId, entryId }) => 
      patientapi.deleteExpressionPrescription(expression_CclId, entryId)
    )
  );
  // Process additions
  if (pendingToAdd.size > 0) {
    const conclusionGroupId = currCclGroupId.value;
    const originalTexts = Array.from(pendingToAdd.values()).map(({ originalText }) => originalText);
    await patientapi.addBioPrescriptionsToConclusion(conclusionGroupId, originalTexts);
  }
  pendingToAdd.clear();
  pendingToDelete.clear();
}

watch(
  () => [
    appUserStore.currentAppUserData,
    currentEntryStore.currentCcl?.conclusionGroupId,
  ],
  () => {
    createLinkedBioGroups();
  },
  { immediate: true }
)

onBeforeUnmount(async () => {
  await flushPendingChanges()
  await fullReload()
})
</script>



<style scoped>
.height-scroll {
  max-height: calc(100vh - 366px);
  overflow-y: auto;
}
.es-suggestion-txt {
  max-width: 27rem;
}
</style>